import { useEffect, useState } from 'react';
import { useAuth } from '@/lib/auth';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
    Rocket, LayoutDashboard, Activity, Layers, Lightbulb, Search, MessageSquare,
    ArrowRight, ArrowLeft, PartyPopper, MousePointerClick, Bell,
} from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Visite guidée du Hub Projets — s'affiche à la première connexion sur /projets,
 * puis rejouable via le bouton "Revoir la visite".
 * Toutes les fonctionnalités clés de l'écosystème sont expliquées.
 */
const SEEN_KEY_PREFIX = 'valnexo_hub_intro_seen_';

const STEPS = [
    {
        icon: PartyPopper,
        color: 'from-indigo-500 to-purple-600',
        title: 'Bienvenue dans les Projets Valnexo',
        body: (
            <>
                Ton nouveau <b>Hub de pilotage</b> pour ne plus jamais laisser filer une idée, une fiche CEE
                ou un chantier interne.<br /><br />
                Un espace centralisé où chaque projet vit dans un <b>kanban</b>, avance étape par étape, et laisse
                une trace complète — mur d&apos;échange, tâches, fichiers.
                <br /><br />
                <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                    <MousePointerClick className="w-3.5 h-3.5" /> Prends 2 minutes — tu vas gagner des heures ensuite.
                </span>
            </>
        ),
    },
    {
        icon: LayoutDashboard,
        color: 'from-blue-500 to-indigo-600',
        title: 'Les compteurs cliquables',
        body: (
            <>
                Les 5 <b>compteurs colorés</b> en haut de la page (Projets actifs, Mes projets, En retard,
                Deadline passée, Clôturés) sont <b>tous cliquables</b>.<br /><br />
                Un clic → tu ouvres la <b>liste filtrée</b> correspondante et tu peux rebasculer entre les vues
                en un clin d&apos;œil. Chaque ligne de la liste te mène directement au projet.
            </>
        ),
    },
    {
        icon: Activity,
        color: 'from-fuchsia-500 to-pink-600',
        title: 'Le fil d\u2019activité',
        body: (
            <>
                Juste en-dessous, tu retrouves le <b>fil d&apos;activité en temps réel</b> : tout ce qui bouge sur les
                projets — <span className="text-indigo-600">messages du mur</span>,{' '}
                <span className="text-fuchsia-600">changements d&apos;étape</span>,{' '}
                <span className="text-amber-600">nouvelles tâches</span>,{' '}
                <span className="text-emerald-600">tâches terminées</span>,{' '}
                <span className="text-purple-600">clôtures</span>,{' '}
                <span className="text-yellow-600">idées</span>.<br /><br />
                Les <b>nouveautés non-vues</b> sont surlignées + une pastille rouge sur le menu. Un clic sur
                n&apos;importe quel item → tu es sur le projet concerné.
            </>
        ),
    },
    {
        icon: Layers,
        color: 'from-teal-500 to-emerald-600',
        title: 'Les espaces de travail',
        body: (
            <>
                Un <b>espace</b> regroupe des projets d&apos;un même domaine (Marketing, R&D, Legal, HUB Technique…).
                Chaque espace a son propre <b>kanban</b> et ses <b>thèmes</b> (Fiche CEE, Dispositif…) avec
                pipelines paramétrables.<br /><br />
                Dans un projet, tu retrouves :
                <ul className="mt-2 space-y-1 text-sm">
                    <li>• <b>Kanban</b> multi-étapes avec plafonds de durée</li>
                    <li>• <b>Mur d&apos;échange</b> (messages, images, PDF, vidéos, vocaux, réactions, mentions)</li>
                    <li>• <b>Tâches multi-assignées</b> synchronisées avec le CRM</li>
                    <li>• <b>Contexte du projet</b> (mini-wiki éditable)</li>
                </ul>
            </>
        ),
    },
    {
        icon: Lightbulb,
        color: 'from-amber-500 to-orange-600',
        title: 'Le mur d\u2019idées & propositions',
        body: (
            <>
                Un <b>espace collaboratif ouvert à tous</b> pour balancer une idée, ouvrir un débat ou proposer un
                projet. Chacun peut <b>voter</b>, <b>commenter</b>, ou proposer de <b>convertir en projet</b> les
                bonnes idées.<br /><br />
                Les nouveaux messages sont <b>mis en avant automatiquement</b> — encadré rouge + tag &quot;NOUVEAU&quot;
                pour que rien ne passe inaperçu. Le mur s&apos;ouvre tout seul dès qu&apos;il y a du neuf.
            </>
        ),
    },
    {
        icon: Search,
        color: 'from-slate-600 to-slate-900',
        title: 'Recherche & alertes',
        body: (
            <>
                <b>Recherche de fichiers</b> : retrouve n&apos;importe quel PDF, image, vidéo ou vocal partagé dans les
                murs, même après plusieurs semaines. Filtrable par espace, par projet et par type.<br /><br />
                <b>Alertes intelligentes</b> : le système te prévient quand une étape traîne trop, quand une deadline
                est dépassée, quand on te mentionne, ou quand une tâche t&apos;est assignée — in-app <b>+</b> email <b>+</b> push.
            </>
        ),
    },
    {
        icon: Bell,
        color: 'from-rose-500 to-red-600',
        title: 'Les pastilles rouges',
        body: (
            <>
                Sur la <b>Sidebar</b> et le <b>menu mobile</b>, l&apos;onglet <b>Projets</b> affiche une pastille rouge
                dès qu&apos;il y a du neuf — messages, changements d&apos;étape, tâches, mentions.<br /><br />
                <b>Sur chaque carte projet</b>, un petit point rouge apparaît aussi si tu as raté un message. Objectif :
                que <b>rien</b> ne t&apos;échappe.
            </>
        ),
    },
    {
        icon: MessageSquare,
        color: 'from-indigo-500 to-blue-600',
        title: 'Astuces bonus',
        body: (
            <>
                <ul className="space-y-2 text-sm">
                    <li>• Chaque <b>«&nbsp;?&nbsp;»</b> en petit à côté d&apos;un titre affiche une aide contextuelle au survol.</li>
                    <li>• Les <b>@mentions</b> dans les murs déclenchent une notification prioritaire pour la personne mentionnée.</li>
                    <li>• Le <b>pipeline</b> peut être <b>personnalisé par projet</b> — pas seulement par thème.</li>
                    <li>• Les <b>accès sont granulaires</b> : un membre peut voir un espace entier OU juste quelques projets isolés.</li>
                    <li>• Tu peux <b>revoir cette visite</b> à tout moment via le bouton <b>«&nbsp;Revoir la visite&nbsp;»</b> en haut de la page.</li>
                </ul>
                <div className="mt-4 flex items-center gap-2 text-emerald-600 font-semibold text-sm">
                    <PartyPopper className="w-4 h-4" /> Prêt à démarrer ? À toi de jouer !
                </div>
            </>
        ),
    },
];

export function HubIntroTour({ forceOpen = false, onClose }) {
    const { user } = useAuth();
    const key = SEEN_KEY_PREFIX + (user?.id || 'anon');
    const [open, setOpen] = useState(false);
    const [step, setStep] = useState(0);

    // Auto-ouvrir à la première visite
    useEffect(() => {
        if (forceOpen) {
            setOpen(true);
            setStep(0);
            return;
        }
        try {
            const seen = localStorage.getItem(key);
            if (!seen) {
                // Petit délai pour laisser la page charger avant d'ouvrir
                const t = setTimeout(() => setOpen(true), 600);
                return () => clearTimeout(t);
            }
        } catch { /* noop */ }
    }, [forceOpen, key]);

    const close = () => {
        setOpen(false);
        try { localStorage.setItem(key, new Date().toISOString()); } catch { /* noop */ }
        onClose?.();
    };

    const isLast = step === STEPS.length - 1;
    const current = STEPS[step];
    const Icon = current.icon;

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
            <DialogContent
                className="max-w-lg p-0 overflow-hidden gap-0 border-0"
                data-testid="hub-intro-tour"
            >
                {/* Header coloré */}
                <div className={cn('bg-gradient-to-br p-6 text-white relative', current.color)}>
                    <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center mb-4">
                        <Icon className="w-8 h-8" />
                    </div>
                    <div className="text-[11px] uppercase tracking-widest text-white/70 font-semibold mb-1">
                        Étape {step + 1} / {STEPS.length}
                    </div>
                    <h2 className="text-2xl font-bold leading-tight">{current.title}</h2>
                </div>
                {/* Contenu */}
                <div className="p-6 text-slate-700 text-sm leading-relaxed max-h-[45vh] overflow-y-auto">
                    {current.body}
                </div>
                {/* Progress dots */}
                <div className="px-6 pb-3 flex items-center justify-center gap-1.5">
                    {STEPS.map((_, i) => (
                        <button
                            key={i}
                            onClick={() => setStep(i)}
                            className={cn(
                                'h-1.5 rounded-full transition-all',
                                i === step ? 'w-6 bg-indigo-500' : 'w-1.5 bg-slate-200 hover:bg-slate-300',
                            )}
                            data-testid={`tour-dot-${i}`}
                            aria-label={`Aller à l'étape ${i + 1}`}
                        />
                    ))}
                </div>
                {/* Footer nav */}
                <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between bg-slate-50">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setStep((s) => Math.max(0, s - 1))}
                        disabled={step === 0}
                        data-testid="tour-prev"
                        className="gap-1"
                    >
                        <ArrowLeft className="w-4 h-4" /> Précédent
                    </Button>
                    {!isLast && (
                        <Button variant="ghost" size="sm" onClick={close} className="text-slate-400 hover:text-slate-600" data-testid="tour-skip">
                            Passer
                        </Button>
                    )}
                    {isLast ? (
                        <Button size="sm" onClick={close} className="gap-1 bg-indigo-600 hover:bg-indigo-700" data-testid="tour-finish">
                            <Rocket className="w-4 h-4" /> C&apos;est parti
                        </Button>
                    ) : (
                        <Button size="sm" onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))} className="gap-1" data-testid="tour-next">
                            Suivant <ArrowRight className="w-4 h-4" />
                        </Button>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
